import { useLayoutEffect, useRef, useState } from "react";
import { captureVideoFrame, releaseCanvasFrame } from "./videoFrame";

/**
 * Present the decoded frame of a paused scrub video without exposing the
 * browser's native Play badge. The caller keeps the source video hidden and
 * places the returned canvas over its existing poster while frameReady.
 * Captures use one reusable canvas, only when a decoded frame is available.
 */
export function useScrubVideoPresentation({ videoRef, enabled = true, onFrameError } = {}) {
  const canvasRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);
  const errorHandlerRef = useRef(onFrameError);
  errorHandlerRef.current = onFrameError;

  useLayoutEffect(() => {
    setFrameReady(false);
    if (!enabled) return undefined;
    const video = videoRef?.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;

    const capture = () => {
      if (video.readyState < 2 || video.seeking || video.videoWidth <= 0 || video.videoHeight <= 0) return;
      try {
        if (!captureVideoFrame(video, canvas)) throw new Error("Scrub frame canvas unavailable");
        setFrameReady(true);
      } catch (error) {
        // A resize may already have cleared the old frame. Let the caller
        // retain its poster/fade fallback instead of exposing paused video.
        setFrameReady(false);
        errorHandlerRef.current?.(error);
      }
    };

    video.addEventListener("loadeddata", capture);
    video.addEventListener("seeked", capture);
    capture();
    return () => {
      video.removeEventListener("loadeddata", capture);
      video.removeEventListener("seeked", capture);
      releaseCanvasFrame(canvas);
    };
  }, [enabled, videoRef]);

  return { canvasRef, frameReady };
}
